import { Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { defer, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { DrawingIssueSet } from '../../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { DrawingsAnalytics, errorProps } from '../drawings-analytics';

export interface DrawingPublishDialogData {
  projectId: string;
  setUuid: string;
  /** The set's change notes as saved */
  notes: string | null;
  /** Change notes can be edited (configure rights) */
  canEditNotes: boolean;
  /** Pages that publish, pages skipped, and those of the publishing pages that replace a current sheet */
  publishCount: number;
  skippedCount: number;
  replaceCount: number;
  /** Wrong-drawing guard: the publishing pages' warnings, by revision, as the review shows them */
  warnings?: Record<string, { page: string; warnings: string[] }>;
  /** Review 2 #2: what blocks the publish at the moment it is sent (page edits saving or failed); '' when nothing does */
  blockedReason?: () => string;
}

/** The API's 409 when kept pages have warnings nobody acknowledged (api 7c9c36c0) */
export const NEEDS_ACKNOWLEDGEMENT = 'DRAWING_SET_NEEDS_ACKNOWLEDGEMENT';
const WARNING_COPY: Record<string, string> = {
  number_mismatch: 'Title block number differs from the sheet',
  low_similarity: 'Looks different from the current revision',
};

/** What the review gets back: the published set, and the notes as they were saved */
export interface DrawingPublishDialogResult {
  set: DrawingIssueSet;
  notes: string;
}

/**
 * Publish confirmation (W46): the change notes belong to the publish, so they are written here. The upload-dialog
 * pattern (Figma 12547:40446): heading, the field, one full-width primary action; closes from outside, no Cancel.
 * Changed notes are saved first, then the set is published; an error stays in the dialog.
 */
@Component({
  selector: 'app-drawing-publish-dialog',
  templateUrl: './drawing-publish-dialog.component.html',
  styleUrls: ['./drawing-publish-dialog.component.scss'],
  standalone: false,
})
export class DrawingPublishDialogComponent {
  readonly data = inject<DrawingPublishDialogData>(MAT_DIALOG_DATA);
  private ref = inject<MatDialogRef<DrawingPublishDialogComponent, DrawingPublishDialogResult | null>>(MatDialogRef);
  private api = inject(ProjectDrawingsService);
  private track = inject(DrawingsAnalytics);
  readonly form = new FormGroup({ notes: new FormControl({ value: this.data.notes ?? '', disabled: !this.data.canEditNotes }) });
  publishing = false;
  error = '';
  /**
   * Review N3: what a dismissal leaves behind for the review. The notes saved in this dialog (null: none), and whether a
   * publish got no answer (status 0), so it may have gone through.
   */
  savedNotes: string | null = null;
  publishUncertain = false;
  /** The pages the API asked about, as list lines; Publish anyway acknowledges them */
  toAcknowledge: { uuid: string; text: string }[] = [];

  /** "523 sheets will be published · 6 skipped · 12 replace current sheets" */
  get summary(): string {
    const { publishCount: n, skippedCount: skipped, replaceCount: replace } = this.data;
    const parts = [`${n} ${n === 1 ? 'sheet will be' : 'sheets will be'} published`];
    if (skipped) {
      parts.push(`${skipped} skipped`);
    }
    if (replace) {
      parts.push(replace === 1 ? '1 replaces a current sheet' : `${replace} replace current sheets`);
    }
    return parts.join(' · ');
  }

  get publishLabel(): string {
    if (this.toAcknowledge.length) {
      return 'Publish anyway';
    }
    const n = this.data.publishCount;
    return `Publish ${n} ${n === 1 ? 'sheet' : 'sheets'}`;
  }

  get notes(): string {
    return this.form.controls.notes.value ?? '';
  }

  /** Against what was saved last (a Publish anyway after a 409 does not save the same notes again) */
  get notesChanged(): boolean {
    return this.data.canEditNotes && this.notes !== (this.savedNotes ?? this.data.notes ?? '');
  }

  get acknowledgeHeading(): string {
    const n = this.toAcknowledge.length;
    return `Check ${n} ${n === 1 ? 'page' : 'pages'} before you publish`;
  }

  publish(): void {
    if (this.publishing) {
      return;
    }
    // Never ahead of page edits still on their way, nor over ones that failed: the set would publish without them
    const blocked = this.data.blockedReason?.() ?? '';
    if (blocked) {
      this.error = blocked;
      return;
    }
    this.publishing = true;
    // Review I3: no backdrop or Esc close while the publish runs, or the review would miss the set it published
    this.ref.disableClose = true;
    this.error = '';
    const notes = this.notes;
    const saveNotes: Observable<unknown> = this.notesChanged
      ? this.api.updateSet(this.data.projectId, this.data.setUuid, notes).pipe(tap(() => (this.savedNotes = notes)), catchError(e => {
        this.track.error('publish_notes', e);
        return throwError(() => e);
      }))
      : of(null);
    const acknowledge = this.toAcknowledge.map(p => p.uuid);
    const publish = defer(() => (acknowledge.length
      ? this.api.publishSet(this.data.projectId, this.data.setUuid, acknowledge)
      : this.api.publishSet(this.data.projectId, this.data.setUuid))).pipe(catchError(e => {
      this.publishUncertain = this.api.isOffline(e);
      if (e?.error?.code !== NEEDS_ACKNOWLEDGEMENT) {
        this.track.track('set_publish_failed', { set_uuid: this.data.setUuid, ...errorProps(e) });
      }
      return throwError(() => e);
    }));
    saveNotes.pipe(switchMap(() => publish)).subscribe({
      next: set => this.ref.close({ set, notes }),
      error: (e: HttpErrorResponse) => {
        this.publishing = false;
        this.ref.disableClose = false;
        if (e?.error?.code === NEEDS_ACKNOWLEDGEMENT && Array.isArray(e.error.details) && e.error.details.length) {
          this.askToAcknowledge(e.error.details);
          return;
        }
        this.error = this.api.failureMessage(e?.error?.code, this.api.errorMessage(e));
      },
    });
  }

  /** The 409's pages, in the review's words where it listed them, else from the warning codes */
  private askToAcknowledge(details: { revision_uuid: string; page_number: number; warnings: string[] }[]): void {
    this.toAcknowledge = details.map(d => {
      const known = this.data.warnings?.[d.revision_uuid];
      const page = known?.page ?? `Page ${d.page_number}`;
      const warnings = known?.warnings ?? (d.warnings ?? []).map(w => WARNING_COPY[w] ?? w);
      return { uuid: d.revision_uuid, text: `${page}: ${warnings.join(' · ')}` };
    });
    this.track.track('publish_warnings_shown', { set_uuid: this.data.setUuid, pages: this.toAcknowledge.length });
  }
}
